/**
 * Duration parsing/formatting, compact metadata joining, and video-embed resolution.
 *
 * Pure functions with no React or store dependencies, so they are directly
 * unit-testable (see src/lib/__tests__/formatting.test.ts).
 */
import type { VideoInfo } from '../types/domain';

/**
 * Converts a "m:ss" display string into total seconds.
 * Tolerates malformed input by treating missing parts as 0.
 */
export function parseLength(str: string): number {
  const [m, s] = str.split(':').map(Number);
  return (m || 0) * 60 + (s || 0);
}

/** Converts seconds into a "m:ss" display string. */
export function formatTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Formats an ISO date as the app's short display label, e.g. "Aug 22". */
export function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * A truthful song/version date label while the item is still recent.
 *
 * The stored `date` field is a snapshot string and can be born as "Just now". `createdAt`
 * is the canonical timestamp, so recent items derive their label from that and only fall
 * back to the stored string when no usable timestamp exists.
 */
export function liveDateLabel(iso: string | undefined, now = Date.now()): string | null {
  if (!iso) return null;
  const stampedAt = new Date(iso).getTime();
  if (!Number.isFinite(stampedAt)) return null;

  const ageMs = Math.max(0, now - stampedAt);
  if (ageMs < MINUTE_MS) return 'Just now';

  const ageMinutes = Math.floor(ageMs / MINUTE_MS);
  if (ageMinutes < 60) return `${ageMinutes} min ago`;

  const ageHours = Math.floor(ageMs / HOUR_MS);
  if (ageHours < 24) return `${ageHours} hr ago`;

  return shortDate(iso);
}

/** Falls back to the stored display string when there is no usable creation time. */
export function songDateLabel(createdAt: string | undefined, fallback: string, now = Date.now()): string {
  return liveDateLabel(createdAt, now) ?? fallback;
}

/**
 * How long until a recent live date label should refresh.
 *
 * Returns null once the label has settled into its day-level short date.
 */
export function nextLiveDateRefreshMs(iso: string | undefined, now = Date.now()): number | null {
  if (!iso) return null;
  const stampedAt = new Date(iso).getTime();
  if (!Number.isFinite(stampedAt)) return null;

  const ageMs = Math.max(0, now - stampedAt);
  if (ageMs >= DAY_MS) return null;

  if (ageMs < MINUTE_MS) return Math.max(1, MINUTE_MS - ageMs);
  if (ageMs < HOUR_MS) {
    const remainder = ageMs % MINUTE_MS;
    return remainder === 0 ? MINUTE_MS : MINUTE_MS - remainder;
  }
  const remainder = ageMs % HOUR_MS;
  return remainder === 0 ? HOUR_MS : HOUR_MS - remainder;
}

/**
 * Joins the present metadata parts with the app's middle-dot separator.
 *
 * Empty strings and absent values are dropped, so a song with no genre prints
 * "3:12 · Just now" rather than " · 3:12 · Just now".
 */
export function joinWithMiddleDot(
  parts: readonly (string | number | false | null | undefined)[]
): string {
  return parts
    .filter((part): part is string | number => part !== null && part !== undefined && part !== false)
    .map((part) => String(part).trim())
    .filter(Boolean)
    .join(' · ');
}

/**
 * Resolves a video URL into an embed URL, a thumbnail, and the provider name.
 *
 * `provider` is returned so the UI can label the "watch externally" link from
 * the actual URL. The link was previously hardcoded to "Watch on YouTube"
 * while DEMO_VIDEO_URL points at Wistia.
 */
export function getVideoInfo(url: string): VideoInfo {
  try {
    const u = new URL(url);

    // Wistia — id can be in ?wvideo= or the last path segment
    if (u.hostname.includes('wistia')) {
      const id = u.searchParams.get('wvideo') || u.pathname.split('/').filter(Boolean).pop();
      if (id) {
        return {
          provider: 'Wistia',
          embedUrl: `https://fast.wistia.net/embed/iframe/${id}`,
          thumbUrl: `https://fast.wistia.com/embed/medias/${id}/swatch`,
        };
      }
    }

    // YouTube
    if (u.hostname.includes('youtu.be')) {
      const id = u.pathname.slice(1);
      return {
        provider: 'YouTube',
        embedUrl: `https://www.youtube.com/embed/${id}`,
        thumbUrl: `https://img.youtube.com/vi/${id}/hqdefault.jpg`,
      };
    }
    if (u.hostname.includes('youtube.com')) {
      const id = u.searchParams.get('v');
      if (id)
        return {
          provider: 'YouTube',
          embedUrl: `https://www.youtube.com/embed/${id}`,
          thumbUrl: `https://img.youtube.com/vi/${id}/hqdefault.jpg`,
        };
    }

    // Vimeo
    if (u.hostname.includes('vimeo.com')) {
      const id = u.pathname.split('/').filter(Boolean).pop();
      return {
        provider: 'Vimeo',
        embedUrl: `https://player.vimeo.com/video/${id}`,
        thumbUrl: null,
      };
    }

    return { provider: null, embedUrl: null, thumbUrl: null };
  } catch {
    return { provider: null, embedUrl: null, thumbUrl: null };
  }
}
